import { useEffect } from 'react'
import { reaction } from 'mobx'
import { toast } from 'react-toastify'
import PushNotificationsStore from './PushNotifications.store'
import UserAlertsStore from './../users/UserAlertsIndicator/UserAlerts.store'

/**
 * Renders nothing. Two jobs:
 *
 *  1. Start push enrolment once, AFTER the app has mounted — never during boot,
 *     so registering the FCM service worker can never race the Workbox
 *     cache-busting worker's install on a first visit.
 *
 *  2. Render foreground messages. The background worker does not run while a
 *     tab is focused, so a message arriving mid-session would otherwise be
 *     invisible.
 *
 * The rendering lives here rather than in the store on purpose: it keeps
 * react-toastify and UserAlerts.store out of the store's import graph, which
 * matters because Auth.service imports the store and those would close an
 * import cycle back through the API client.
 *
 * The store subscribes to Firebase auth itself, so this is safe to mount above
 * the router and before anyone has signed in.
 */
const PushNotificationsBootstrap = () => {
  useEffect(() => {
    PushNotificationsStore.init()

    // The service worker cannot navigate this tab itself — WindowClient.navigate()
    // requires it to be the controlling worker, and ours controls no pages by
    // design (narrow scope, so it cannot displace the Workbox cache-busting
    // worker). So on a notification click it focuses the tab and posts the
    // target here instead.
    //
    // The URL carries ?push=<id>, so the normal load path reports the click and
    // strips the param — no separate reporting branch to keep in sync.
    const onSwMessage = (event) => {
      const data = event && event.data
      if (!data || data.type !== 'usabg-push-click' || !data.url) return
      try {
        window.location.assign(data.url)
      } catch (ex) {
        console.error('[push] failed to open notification target', ex)
      }
    }

    let swListening = false
    try {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('message', onSwMessage)
        swListening = true
      }
    } catch (ex) {
      console.error('[push] could not listen for worker messages', ex)
    }

    // React to the tick, not the payload — two identical messages are still
    // two events.
    const dispose = reaction(
      () => PushNotificationsStore.messageTick,
      () => {
        const data = PushNotificationsStore.lastMessage
        if (!data) return

        // A real OS notification, not just a toast. See
        // showForegroundNotification() — FCM treats a tab as "foreground" even
        // when the agent is working in another application, so a toast alone
        // would be missed by exactly the people we most need to reach.
        PushNotificationsStore.showForegroundNotification(data)

        const text = data.title
          ? `${data.title}${data.body ? ` — ${data.body}` : ''}`
          : data.body || 'You have a new message.'

        try {
          toast.info(text, {
            position: toast.POSITION.TOP_RIGHT,
            // A critical message should not disappear on its own.
            autoClose: data.severity === 'critical' ? false : 8000,
          })
        } catch (ex) {
          console.error('[push] foreground toast failed', ex)
        }

        // The bell derives its badge from the alerts it fetches. Bumping this
        // counter is what re-triggers that fetch (UserAlertsIndicator's effect
        // depends on it), so the badge updates from real server data rather
        // than an optimistic guess.
        try {
          UserAlertsStore.setNumberOfAlerts(
            (UserAlertsStore.numberOfAlerts || 0) + 1
          )
        } catch (ex) {
          console.error('[push] alert badge bump failed', ex)
        }
      }
    )

    return () => {
      dispose()
      if (swListening) {
        try {
          navigator.serviceWorker.removeEventListener('message', onSwMessage)
        } catch (ex) {}
      }
    }
  }, [])

  return null
}

export default PushNotificationsBootstrap
